import React, { useState, useMemo, forwardRef, useImperativeHandle, useEffect } from "react";
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from "primereact/inputnumber";
import { useGlobalContext } from "../Config";
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { normalizeFicha } from "../utilities/fichaNormalizer";
        


// Configuración del molde del formulario
const FORM_TEMPLATE = {
    generales: {
        title: "Información General",
        icon: "pi pi-info-circle",
        fields: {
        porton_tipo: { type: "dropdown", label: "Tipo de portón", options: [{label: "Corredera", value: "corredera"}, {label: "Basculante", value: "basculante"}, {label: "Seccional", value: "seccional"}] },
        porton_ancho_m: { type: "number", label: "Ancho portón (m)", placeholder: "ej: 3.5" },
        porton_alto_m: { type: "number", label: "Alto portón (m)", placeholder: "ej: 3.0" },
        electricidad_fase: { type: "dropdown", label: "Fase eléctrica", options: [{label: "Monofásico", value: "monofasico"}, {label: "Trifásico", value: "trifasico"}] },
        electricidad_amperaje: { type: "number", label: "Amperaje (A)", placeholder: "ej: 25" },
        ventilacion: { type: "dropdown", label: "Ventilación", options: [{label: "Natural", value: "natural"}, {label: "Forzada", value: "forzada"}, {label: "Mecánica", value: "mecanica"}] }
        }
    },
    caracteristicas: {
        title: "Características Físicas",
        icon: "pi pi-building",
        fields: {
        altura_m_hombro: { type: "number", label: "Altura al hombro (m)", placeholder: "ej: 4.5" },
        altura_maxima_m: { type: "number", label: "Altura máxima (m)", placeholder: "ej: 6" },
        piso: { type: "text", label: "Tipo de piso", placeholder: "ej: Hormigón H25 15 cm" },
        capacidad_carga_kg: { type: "number", label: "Capacidad de carga (kg)", placeholder: "ej: 5000" }
        }
    },
    servicios: {
        title: "Servicios e Instalaciones",
        icon: "pi pi-cog",
        fields: {
        baño: { type: "checkbox", label: "Baño" },
        oficina: { type: "checkbox", label: "Oficina" },
        estanterias: { type: "checkbox", label: "Estanterías incluidas" },
        montacargas: { type: "checkbox", label: "Montacargas disponible" }
        }
    },
    accesos: {
        title: "Accesos y Seguridad",
        icon: "pi pi-shield",
        fields: {
        acceso_camiones: { type: "checkbox", label: "Acceso para camiones" },
        rampa: { type: "checkbox", label: "Rampa de acceso" },
        camaras: { type: "checkbox", label: "Cámaras de seguridad" },
        cerco: { type: "checkbox", label: "Cerco perimetral" },
        guardiania: { type: "checkbox", label: "Guardianía" },
        alarma: { type: "checkbox", label: "Sistema de alarma" }
        },
    },

    observaciones: {
        title: "Observaciones",
        icon: "pi pi-comment",
        fields: {
        observaciones: { type: "textarea", label: "Observaciones generales", placeholder: "Información adicional..." }
        }
    }
};

// Moldes específicos por perfil
const PROFILE_TEMPLATES = {
    frigorifica: {
        frio: {
        title: "Información Frigorífica",
        icon: "pi pi-snowflake",
        fields: {
            temp_min: { type: "number", label: "Temperatura mínima (°C)", placeholder: "ej: -25" },
            temp_max: { type: "number", label: "Temperatura máxima (°C)", placeholder: "ej: 5" },
            capacidad_frio_kW: { type: "number", label: "Capacidad frigorífica (kW)", placeholder: "ej: 50" },
            consumo_kWh: { type: "number", label: "Consumo energético (kWh)", placeholder: "ej: 100" },
            clasificacion_energetica: { type: "dropdown", label: "Clasificación energética", options: [{label: "A", value: "A"}, {label: "B", value: "B"}, {label: "C", value: "C"}] }
        }
        }
    },
    farmaceutica: {
        pharma: {
        title: "Información Farmacéutica",
        icon: "pi pi-heart",
        fields: {
            temp_min: { type: "number", label: "Temperatura mínima (°C)", placeholder: "ej: 15" },
            temp_max: { type: "number", label: "Temperatura máxima (°C)", placeholder: "ej: 25" },
            humedad_min: { type: "number", label: "Humedad mínima (%)", placeholder: "ej: 45" },
            humedad_max: { type: "number", label: "Humedad máxima (%)", placeholder: "ej: 55" },
            refrigeracion: { type: "checkbox", label: "Sistema de refrigeración" },
            humidificacion: { type: "checkbox", label: "Sistema de humidificación" },
            protocolos_limpieza: { type: "checkbox", label: "Protocolos de limpieza" },
            control_acceso: { type: "checkbox", label: "Control de acceso especializado" }
        }
        }
    },
    quimicos: {
        quimicos: {
        title: "Información para Químicos",
        icon: "pi pi-flask",
        fields: {
            renovaciones_aire: { type: "number", label: "Renovaciones de aire por hora", placeholder: "ej: 6" },
            estanterias_especificas: { type: "checkbox", label: "Estanterías específicas" },
            separacion_incompatibles: { type: "checkbox", label: "Separación de incompatibles" },
            contencion_derrame: { type: "checkbox", label: "Contención de derrames" },
            epp_disponible: { type: "checkbox", label: "EPP disponible" }
        }
        }
    },
    logistica_urbana: {
        logistica: {
        title: "Logística Urbana",
        icon: "pi pi-truck",
        fields: {
            ubicacion_estrategica: { type: "checkbox", label: "Ubicación estratégica" },
            andenes_carga: { type: "number", label: "Andenes de carga", placeholder: "ej: 4" },
            tiempos_carga: { type: "number", label: "Tiempos de carga/descarga (min)", placeholder: "ej: 15" },
            optimizacion: { type: "dropdown", label: "Optimización", options: [{label: "E-commerce < 24h", value: "ecommerce_24h"}, {label: "Distribución urbana", value: "distribucion_urbana"}] }
        }
        }
    }
};

const PERFIL_OPTS = [
    { value: "estandar", label: "Estándar" },
    { value: "frigorifica", label: "Frigorífica" },
    { value: "quimicos", label: "Químicos" },
    { value: "farmaceutica", label: "Farmacéutica" },
    { value: "logistica_urbana", label: "Logística urbana" },
];

// Función para convertir texto a snake_case
function toSnakeCase(text) {
    return text
        .toLowerCase()
        .replace(/[áàäâ]/g, 'a')
        .replace(/[éèëê]/g, 'e')
        .replace(/[íìïî]/g, 'i')
        .replace(/[óòöô]/g, 'o')
        .replace(/[úùüû]/g, 'u')
        .replace(/[ñ]/g, 'n')
        .replace(/[ç]/g, 'c')
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
}

// Función para generar el estado inicial desde el molde
function generateInitialState(template, profileTemplates = {}) {
    const state = {
        perfil: "estandar",
        sections: {},
        customSections: []
    };

    // Generar secciones base
    Object.entries(template).forEach(([sectionKey, sectionConfig]) => {
        state.sections[sectionKey] = {
        title: sectionConfig.title,
        icon: sectionConfig.icon,
        fields: {}
        };
        
        Object.entries(sectionConfig.fields).forEach(([fieldKey, fieldConfig]) => {
        state.sections[sectionKey].fields[fieldKey] = getDefaultValue(fieldConfig.type);
        });
    });

     // Secciones del perfil seleccionado (si las hay)
    const profileSections = (profileTemplates && typeof profileTemplates === 'object') ? profileTemplates : {};
    Object.entries(profileSections).forEach(([sectionKey, sectionConfig]) => {
        state.sections[sectionKey] = {
            title: sectionConfig.title,
            icon: sectionConfig.icon,
            fields: {}
        };
        Object.entries(sectionConfig.fields || {}).forEach(([fieldKey, fieldConfig]) => {
            state.sections[sectionKey].fields[fieldKey] = getDefaultValue(fieldConfig.type);
        });
    });


    return state;
}

// Función para obtener valor por defecto según tipo de campo
function getDefaultValue(type) {
    switch (type) {
        case "number": return "";
        case "text": 
        case "textarea": return "";
        case "checkbox": return false;
        case "date": return null;
        default: return "";
    }
}

// Función para renderizar un campo dinámicamente
function renderField(fieldKey, fieldConfig, value, onChange, sectionKey) {
    const fieldId = `${sectionKey}-${fieldKey}`;

    switch (fieldConfig.type) {
        case "number":
        return (
            <span key={fieldKey} className="p-float-label mt-2 mb-3">
            <InputNumber
                id={fieldId}
                value={value}
                onValueChange={(e) => onChange(e.value)}
                className="p-inputtext-lg"
                placeholder={fieldConfig.placeholder}
            />
            <label htmlFor={fieldId}>
                {fieldConfig.icon && <i className={`${fieldConfig.icon} mr-2`}></i>}
                {fieldConfig.label}
            </label>
            </span>
        );
        
        case "text":
        return (
            <span key={fieldKey} className="p-float-label mt-2 mb-3">
            <InputText
                id={fieldId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="p-inputtext-lg"
                placeholder={fieldConfig.placeholder}
            />
            <label htmlFor={fieldId}>
                {fieldConfig.icon && <i className={`${fieldConfig.icon} mr-2`}></i>}
                {fieldConfig.label}
            </label>
            </span>
        );
        
        case "textarea":
        return (
            <span key={fieldKey} className="p-float-label mt-2 mb-3">
            <InputTextarea
                id={fieldId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="p-inputtext p-inputtext-lg w-full"
                placeholder={fieldConfig.placeholder}
                rows={3}
            />
            <label htmlFor={fieldId}>
                {fieldConfig.icon && <i className={`${fieldConfig.icon} mr-2`}></i>}
                {fieldConfig.label}
            </label>
            </span>
        );
        
        case "dropdown":
        return (
            <span key={fieldKey} className="p-float-label mt-2 mb-3">
            <Dropdown
                id={fieldId}
                value={value}
                options={fieldConfig.options}
                onChange={(e) => onChange(e.value)}
                className="p-inputtext-lg w-full"
                placeholder={fieldConfig.placeholder}
            />
            <label htmlFor={fieldId}>
                {fieldConfig.icon && <i className={`${fieldConfig.icon} mr-2`}></i>}
                {fieldConfig.label}
            </label>
            </span>
        );
        
        case "checkbox":
        return (
            <label key={fieldKey} className="flex items-center gap-2 text-sm mt-2 mb-3">
            <Checkbox
                inputId={fieldId}
                checked={value}
                onChange={(e) => onChange(e.checked)}
            />
            {fieldConfig.icon && <i className={`${fieldConfig.icon} mr-1`}></i>}
            {fieldConfig.label}
            </label>
        );
        
        case "date":
        return (
            <span key={fieldKey} className="p-float-label mt-2 mb-3">
            <Calendar
                id={fieldId}
                value={value}
                onChange={(e) => onChange(e.value)}
                className="p-inputtext-lg"
                placeholder={fieldConfig.placeholder}
                dateFormat="dd/mm/yy"
            />
            <label htmlFor={fieldId}>
                {fieldConfig.icon && <i className={`${fieldConfig.icon} mr-2`}></i>}
                {fieldConfig.label}
            </label>
            </span>
        );
        
        default:
        return null;
    }
}

const FormCrearProductoFicha = forwardRef(({ 
    idProducto,
    perfil,
    ficha,
    isEditing = false
}, ref) => {
    const { baseUrl } = useGlobalContext();
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    const [s, setS] = useState(() => {
        const initialTemplate = { ...FORM_TEMPLATE };
        const initialProfileKey = perfil || 'estandar';
        const initialState = generateInitialState(initialTemplate, PROFILE_TEMPLATES[initialProfileKey] || {});
        initialState.perfil = initialProfileKey;
        return initialState;
    });

    const [showAddSectionDialog, setShowAddSectionDialog] = useState(false);
    const [showAddFieldDialog, setShowAddFieldDialog] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [newSectionName, setNewSectionName] = useState("");
    const [newSectionTitle, setNewSectionTitle] = useState("");
    const [newFieldName, setNewFieldName] = useState("");
    const [newFieldLabel, setNewFieldLabel] = useState("");
    const [newFieldType, setNewFieldType] = useState("text");

    // useEffect para cargar datos existentes cuando se está editando
    useEffect(() => {
        const loadExistingFicha = async () => {
            if (isEditing && idProducto) {
                try {
                    setLoading(true);
                    const response = await fetch(`${baseUrl}getProductoFicha?idProducto=${idProducto}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json; charset=utf-8',
                            'Accept': 'application/json; charset=utf-8'
                        }
                    });
                    
                    if (response.ok) {
                        const textData = await response.text();
                        const data = JSON.parse(textData);
                        
                        if (data.ficha) {
                            const fichaData = normalizeFicha(data.ficha);
                            
                            // Generar el estado inicial con el template
                            const initialState = generateInitialState(FORM_TEMPLATE, PROFILE_TEMPLATES[data.perfil] || {});
                            
                            // Mapear los datos de la ficha al estado del componente
                        const updatedSections = { ...initialState.sections };
                        
                        // Crear el template combinado para verificar qué secciones son personalizadas
                        const combinedTemplate = {
                            ...FORM_TEMPLATE,
                            ...(PROFILE_TEMPLATES[data.perfil] || {})
                        };
                        
                        // Iterar sobre las secciones de la ficha
                        Object.keys(fichaData).forEach(sectionKey => {
                            // Verificar si la sección existe en el template
                            const isCustomSection = !combinedTemplate[sectionKey];
                            
                            if (updatedSections[sectionKey]) {
                                // Sección existente en template - actualizar campos
                                Object.keys(fichaData[sectionKey]).forEach(fieldKey => {
                                    if (updatedSections[sectionKey].fields[fieldKey] !== undefined) {
                                        updatedSections[sectionKey].fields[fieldKey] = fichaData[sectionKey][fieldKey];
                                    } else {
                                        // Campo personalizado en sección existente
                                        updatedSections[sectionKey].fields[fieldKey] = fichaData[sectionKey][fieldKey];
                                    }
                                });
                            } else if (isCustomSection) {
                                // Sección personalizada - crear nueva sección
                                updatedSections[sectionKey] = {
                                    title: sectionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                    icon: 'pi pi-cog',
                                    isCustom: true,
                                    fields: { ...fichaData[sectionKey] }
                                };
                            }
                        });
                            
                            setS({
                                perfil: data.perfil || 'estandar',
                                sections: updatedSections,
                                customSections: initialState.customSections
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error al cargar la ficha técnica:', error);
                    setMsg('Error al cargar los datos de la ficha técnica');
                } finally {
                    setLoading(false);
                }
            }
        };
    
        loadExistingFicha();
    }, [isEditing, idProducto, baseUrl]);

    // Obtener template actual basado en el perfil
    const currentTemplate = useMemo(() => {
        const baseTemplate = { ...FORM_TEMPLATE };
        if (s.perfil !== "estandar" && PROFILE_TEMPLATES[s.perfil]) {
        Object.assign(baseTemplate, PROFILE_TEMPLATES[s.perfil]);
        }
        return baseTemplate;
    }, [s.perfil]);

    // Función para actualizar un campo
    const updateField = (sectionKey, fieldKey, value) => {
        setS(prev => ({
        ...prev,
        sections: {
            ...prev.sections,
            [sectionKey]: {
            ...prev.sections[sectionKey],
            fields: {
                ...prev.sections[sectionKey].fields,
                [fieldKey]: value
            }
            }
        }
        }));
    };

    // Función para agregar nueva sección
    const addSection = () => {
        if (!newSectionTitle) return;
        
        const generatedName = toSnakeCase(newSectionTitle);
        
        setS(prev => ({
        ...prev,
        sections: {
            ...prev.sections,
            [generatedName]: {
            title: newSectionTitle,
            icon: "pi pi-plus",
            fields: {},
            isCustom: true
            }
        }
        }));
        
        setNewSectionName("");
        setNewSectionTitle("");
        setShowAddSectionDialog(false);
    };

    // Función para eliminar sección
    const removeSection = (sectionKey) => {
        setS(prev => {
        const newSections = { ...prev.sections };
        delete newSections[sectionKey];
        return {
            ...prev,
            sections: newSections
        };
        });
    };

    // Función para agregar nuevo campo
    const addField = () => {
        if (!selectedSection || !newFieldLabel) return;
        
        const generatedName = toSnakeCase(newFieldLabel);
        
        setS(prev => ({
        ...prev,
        sections: {
            ...prev.sections,
            [selectedSection]: {
            ...prev.sections[selectedSection],
            fields: {
                ...prev.sections[selectedSection].fields,
                [generatedName]: getDefaultValue(newFieldType)
            }
            }
        }
        }));
        
        // Actualizar el template dinámicamente
        if (!currentTemplate[selectedSection]) {
        currentTemplate[selectedSection] = { fields: {} };
        }
        currentTemplate[selectedSection].fields[generatedName] = {
        type: newFieldType,
        label: newFieldLabel
        };
        
        setNewFieldName("");
        setNewFieldLabel("");
        setNewFieldType("text");
        setSelectedSection(null);
        setShowAddFieldDialog(false);
    };

    // Función para eliminar campo
    const removeField = (sectionKey, fieldKey) => {
        setS(prev => {
        const newFields = { ...prev.sections[sectionKey].fields };
        delete newFields[fieldKey];
        return {
            ...prev,
            sections: {
            ...prev.sections,
            [sectionKey]: {
                ...prev.sections[sectionKey],
                fields: newFields
            }
            }
        };
        });
    };

    // Función para limpiar todo el formulario
    const clearForm = () => {
        setS(generateInitialState(FORM_TEMPLATE, PROFILE_TEMPLATES));
    };

    // Generar JSON final
    const fichaJson = useMemo(() => {
        // Validar que s.sections existe antes de procesarlo
        if (!s || !s.sections) {
            return JSON.stringify({});
        }
        
        const result = {};
        
        Object.entries(s.sections).forEach(([sectionKey, section]) => {
        // Validar que la sección y sus campos existen
        if (!section || !section.fields) {
            return;
        }
        
        const sectionData = {};
        Object.entries(section.fields).forEach(([fieldKey, value]) => {
            if (value !== "" && value !== null && value !== false) {
            sectionData[fieldKey] = value;
            }
        });
        
        if (Object.keys(sectionData).length > 0) {
            result[sectionKey] = sectionData;
        }
        });
        
        return JSON.stringify(result);
    }, [s]);

    // Exponer datos al componente padre
    useImperativeHandle(ref, () => ({
        getData: () => ({
        perfil: s.perfil,
        fichaJson
        })
    }));

    return (
        <div>
        {loading && (
            <div className="p-3 text-center">
            <i className="pi pi-spin pi-spinner text-2xl"></i>
            <div className="mt-2">Cargando...</div>
            </div>
        )}
        
        {/* Controles principales */}
        <div className="card mb-4">
            <div className="flex justify-content-between align-items-center mb-3">
            <h2 className="text-lg font-medium">Formulario</h2>
            <div className="flex gap-2">
                <Button
                type="button"
                icon="pi pi-plus"
                label="Agregar Sección"
                className="p-button-outlined p-button-sm p-button-rounded"
                onClick={() => setShowAddSectionDialog(true)}
                />
                <Button
                type="button"
                icon="pi pi-trash"
                label="Limpiar Todo"
                className="p-button-outlined p-button-danger p-button-sm p-button-rounded"
                onClick={clearForm}
                />
            </div>
            </div>
            
            {/* Selector de perfil */}
            <div className="mb-3">
            <Dropdown
                value={s.perfil}
                options={PERFIL_OPTS}
                onChange={(e) => {
                    const newState = generateInitialState(FORM_TEMPLATE, PROFILE_TEMPLATES[e.value] || {});
                    newState.perfil = e.value;
                    newState.tagsText = s.tagsText;
                    setS(newState);
                }}
                placeholder="Seleccione un perfil"
                className="w-full"
            />
            </div>

        </div>

        {/* Renderizar secciones dinámicamente */}
        {s && s.sections && Object.entries(s.sections).map(([sectionKey, section]) => {
            const templateSection = currentTemplate[sectionKey];
            if (!templateSection && !section.isCustom) return null;
            
            return (
            <div key={sectionKey} className="card mb-4">
                <div className="flex justify-content-between align-items-center mb-3">
                <h3 className="text-md font-medium">
                    {section.icon && <i className={`${section.icon} mr-2`}></i>}
                    {section.title}
                </h3>
                <div className="flex gap-2">
                    <Button
                    type="button"
                    icon="pi pi-plus"
                    className="p-button-rounded p-button-outlined p-button-sm"
                    onClick={() => {
                        setSelectedSection(sectionKey);
                        setShowAddFieldDialog(true);
                    }}
                    tooltip="Agregar campo"
                    />
                    {section.isCustom && (
                    <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-danger p-button-sm"
                        onClick={() => removeSection(sectionKey)}
                        tooltip="Eliminar sección"
                    />
                    )}
                </div>
                </div>
                
                
                {section.fields && Object.entries(section.fields).map(([fieldKey, value]) => {
                    const fieldConfig = templateSection?.fields?.[fieldKey] || {
                    type: "text",
                    label: fieldKey.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                    };
                    
                    // Verificar condiciones
                    if (fieldConfig.conditional) {
                    const conditionValue = section.fields[fieldConfig.conditional.field];
                    if (conditionValue !== fieldConfig.conditional.value) {
                        return null;
                    }
                    }
                    
                    return (
                    <div key={fieldKey} className="flex items-center gap-2">
                        <div className="flex-1">
                        {renderField(
                        fieldKey,
                        fieldConfig,
                        value,
                        (newValue) => updateField(sectionKey, fieldKey, newValue),
                        sectionKey
                        )}
                        </div>
                        <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-rounded p-button-sm mt-1"
                        onClick={() => removeField(sectionKey, fieldKey)}
                        tooltip="Eliminar campo"
                        style={{ width: '14px', height: '14px', fontSize: '5px'}}
                        />
                    </div>
                    );
                })}
                
                {(!section.fields || Object.keys(section.fields).length === 0) && (
                <div className="text-center text-gray-500 py-4">
                    No hay campos en esta sección. Haz clic en + para agregar campos.
                </div>
                )}
            </div>
            );
        })}

        {/* Diálogo para agregar sección */}
        <Dialog
            header="Agregar Nueva Sección"
            visible={showAddSectionDialog}
            onHide={() => setShowAddSectionDialog(false)}
            className="p-fluid product-dialog"
        >
            <div className="p-fluid">
            <div className="field">
                <label htmlFor="sectionTitle">Título de la sección</label>
                <InputText
                id="sectionTitle"
                value={newSectionTitle}
                onChange={(e) => {
                    setNewSectionTitle(e.target.value);
                    setNewSectionName(toSnakeCase(e.target.value));
                }}
                placeholder="ej: Nueva Sección"
                />
            </div>

            <div className="flex justify-content-end gap-2 mt-3">
                <Button
                type="button"
                label="Cancelar"
                className="p-button-outlined p-button-rounded"
                onClick={() => setShowAddSectionDialog(false)}
                />
                <Button
                type="button"
                label="Agregar"
                onClick={addSection} 
                className="p-button-rounded p-button-primary p-button"
                raised
                disabled={!newSectionTitle}
                />
            </div>
            </div>
        </Dialog>

        {/* Diálogo para agregar campo */}
        <Dialog
            header="Agregar Nuevo Campo"
            visible={showAddFieldDialog}
            onHide={() => setShowAddFieldDialog(false)}
            className="p-fluid product-dialog"
        >
            <div className="p-fluid">
            <div className="field">
                <label htmlFor="fieldLabel">Etiqueta del campo</label>
                <InputText
                id="fieldLabel"
                value={newFieldLabel}
                onChange={(e) => {
                    setNewFieldLabel(e.target.value);
                    setNewFieldName(toSnakeCase(e.target.value));
                }}
                placeholder="ej: Nuevo Campo"
                />
            </div>
            {/* <div className="field">
                <label htmlFor="fieldName">Nombre del campo (generado automáticamente)</label>
                <InputText
                id="fieldName"
                value={newFieldName}
                readOnly
                className="p-inputtext-sm"
                style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
            </div> */}
            <div className="field">
                <label htmlFor="fieldType">Tipo de campo</label>
                <Dropdown
                id="fieldType"
                value={newFieldType}
                options={[
                    { label: "Texto", value: "text" },
                    { label: "Número", value: "number" },
                    { label: "Área de texto", value: "textarea" },
                    { label: "Casilla de verificación", value: "checkbox" },
                    { label: "Fecha", value: "date" }
                ]}
                onChange={(e) => setNewFieldType(e.value)}
                className="w-full"
                />
            </div>
            <div className="flex justify-content-end gap-2 mt-3">
                <Button
                    type="button"
                    label="Cancelar"
                    className="p-button-outlined p-button-rounded"
                    onClick={() => setShowAddFieldDialog(false)}
                />
                <Button
                    type="button"
                    label="Agregar"
                    onClick={addField} 
                    className="p-button-rounded p-button-primary p-button"
                    raised
                    disabled={!newFieldLabel}
                />
            </div>
            </div>
        </Dialog>

        {msg && (
            <pre className={`text-xs whitespace-pre-wrap p-2 rounded border mt-3 ${
            msg.type === "ok" ? "border-green-500" : "border-red-500"
            }`}>
            {msg.text}
            </pre>
        )}
        </div>
    );
});

export default FormCrearProductoFicha;